import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import type { ITemplateMetadata } from '@/store/templateManagement/types';
import type { IRootState } from '..';
import { RecipientRole } from '../../common/recipients/types';
import { envelopeApi } from '../../lib/api/envelope';
import * as templateApi from '../../lib/api/template/template-api';
import pdfCache from '../../lib/pdfCache';
import type { IResponse } from '../../lib/types';
import { setLoading, errorToast } from '../pageFrame/actions';
import type { ITagType } from '../pageFrame/types';
import type { IEnvelope, IPageDimensions } from '../senderBuild/types';
import type { ITemplate, ITemplateTag } from '../templateCreate/template-types';
import * as actions from './actions';
import type { IBlockWithSignerNumber, ITemplateAppliedFromDynamoDB, ITemplatesAppliedFromMongoDB } from './types';

const { ActionTypes } = actions;

function* fetchTemplateBlocks(templateId: string) {
  const res: templateApi.ITemplateResponse<Partial<ITemplate>> = yield call(templateApi.getTemplatePreview, templateId);

  if (res.status !== 200) {
    throw Error(`Non-200 Status Code of Template GET: ${res.status}`);
  }

  const templatePreview = res.result;
  const recipientIds = templatePreview.recipients?.filter((r) => r.role === RecipientRole.Signer).map((r) => r.id);
  const blocksFromAllDocs: IBlockWithSignerNumber[] = [];
  let pagesBeforeThisDoc = 0;

  templatePreview.documents?.forEach((document) => {
    document.blocks?.forEach((block) => {
      const reIndexedBlock: IBlockWithSignerNumber = {
        ...block,
        signerNumber: recipientIds?.indexOf(block.assignedTo!) ?? 0,
        pageNumber: block.pageNumber + pagesBeforeThisDoc,
      };
      blocksFromAllDocs.push(reIndexedBlock);
    });

    pagesBeforeThisDoc += document.numPages ?? 0;
  });

  return blocksFromAllDocs;
}

function getUserTemplateSettings(state: IRootState) {
  return state.pageFrame.settings?.account.template;
}

function validateFlashCodes(flashCodes: ITemplateTag[], queryFlashCodes: string[]): boolean {
  if (!queryFlashCodes.length) {
    return true;
  }
  let valid = false;
  flashCodes.forEach((tag) => {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    if (queryFlashCodes.includes(tag.value)) {
      valid = true;
    }
  });
  return valid;
}

export function* handleFetchEnvelope(action: ReturnType<typeof actions.fetchEnvelope>) {
  try {
    yield put(setLoading(true));
    const res: IResponse<IEnvelope> = yield call(envelopeApi.getEnvelopeById, action.payload);

    if (res.statusCode !== 200) throw Error(`Non-200 Status Code of Envelope GET: ${res.statusCode}`);
    if (!res.success) throw Error('Error fetching envelope');

    const envelope = res.result;
    if (envelope.status !== 'Draft' && envelope.status !== 'Correcting') {
      throw Error('Envelope already sent');
    }

    const docBlobs: ArrayBuffer[] = yield all(envelope.documents.map((doc) => doc.presignedURL));
    const pageDimensions: IPageDimensions = {};
    envelope.documents.forEach((doc, i) => {
      pdfCache[doc.documentId] = docBlobs[i];
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      pageDimensions[doc.documentId] = doc.pages;
    });
    yield put(actions.fetchEnvelopeDone(envelope, pageDimensions));
  } catch {
    yield put(errorToast('Failed to fetch the envelope information, please refresh!'));
  } finally {
    yield put(setLoading(false));
  }
}

export function* handleFetchTemplates(action: ReturnType<typeof actions.fetchTemplates>) {
  try {
    const res: templateApi.ITemplateResponse<ITemplateMetadata[]> = yield call(
      templateApi.getTemplates,
      // prettier-ignore
      { fetchShares: !!action.payload.fetchShares }
    );
    const templates = res.result!;
    if (res.status !== 200) {
      throw Error(`Non-200 Status Code of Templates GET: ${res.status}`);
    }
    if (action.payload.templateQueryParams) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      const settings = yield select(getUserTemplateSettings);
      const stateCodeSetting = settings.tagging.tagTypes.find((tag: ITagType) => tag.name === 'State');
      const flashCodeSetting = settings.tagging.tagTypes.find((tag: ITagType) => tag.name === 'Flash Code(s)');
      const roleSetting = settings.tagging.tagTypes.find((tag: ITagType) => tag.name === 'Role');
      const filteredTemplates: ITemplateMetadata[] = templates.filter((template) => {
        const flashTag = template.tags.filter((tag) => tag.tagTypeId === flashCodeSetting.id);
        const stateTag = template.tags.find((tag) => tag.tagTypeId === stateCodeSetting.id);

        if (!stateTag || stateTag.value !== action.payload.templateQueryParams?.state) {
          return false;
        }

        if (!validateFlashCodes(flashTag, action.payload.templateQueryParams.flashCodes)) {
          return false;
        }
        const roleTag = template.tags.find((tag) => tag.tagTypeId === roleSetting.id);
        if (
          !roleTag ||
          roleTag.value.toLocaleLowerCase() !== action.payload.templateQueryParams.role.toLocaleLowerCase()
        ) {
          return false;
        }
        return true;
      });
      yield put(actions.fetchTemplatesDone(filteredTemplates));
    } else {
      yield put(actions.fetchTemplatesDone(templates));
    }
  } catch {
    yield put(errorToast('Failed to fetch the template information, please refresh!'));
  }
}

export function* handlePreviewTemplate(action: ReturnType<typeof actions.previewTemplate>) {
  if (!action.payload) {
    return;
  }
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const blocks = yield fetchTemplateBlocks(action.payload);
    yield put(actions.previewTemplateDone(blocks));
  } catch {
    yield put(errorToast('Failed to fetch the template information, please refresh!'));
  }
}

export function* handleBulkTemplates(action: ReturnType<typeof actions.finalizeBulkTemplates>) {
  try {
    const { envelopeId, templatesList } = action.payload;
    const res: templateApi.ITemplateResponse<ITemplatesAppliedFromMongoDB> = yield call(
      templateApi.applyTemplatesInBulk,
      envelopeId,
      templatesList
    );
    if (res.status !== 200) {
      throw Error(`Non-200 Status Code of Bulk Template Apply PUT: ${res.status}`);
    }
    yield put(actions.finalizeBulkTemplatesDone());
  } catch (error: any) {
    const errorMessage = error?.message || 'Error updating templates applied';
    yield put(errorToast(errorMessage));
  }
}

export function* handleFetchTemplatesApplied(action: ReturnType<typeof actions.fetchTemplatesApplied>) {
  try {
    const res: templateApi.ITemplateResponse<ITemplatesAppliedFromMongoDB | ITemplateAppliedFromDynamoDB[]> =
      yield call(templateApi.getTemplatesApplied, action.payload.envelopeId);
    const { useMongoDBModel } = action.payload;
    const responseCodes = [200, 204];
    if (!responseCodes.includes(res.status)) {
      throw Error(`Non-Successful Status Code of Fetching Applied Templates POST: ${res.status}`);
    }
    if (!useMongoDBModel) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      for (let i = 0; i < res.result.length; i++) {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        const blocks = yield fetchTemplateBlocks(res.result[i].templateId);
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        res.result[i].blocks = blocks;
      }
      // @ts-expect-error -- This code was written prior to enforcing lint rules
    } else if (res.result.templatesApplied?.length > 0) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      res.result.blockMapping = [];

      // @ts-expect-error -- This code was written prior to enforcing lint rules
      const allBlocksForAppliedTemplates = yield all(
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        res.result.templatesApplied.map((template: string) => fetchTemplateBlocks(template))
      );
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      res.result.templatesApplied.forEach((template, i) => {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        res.result.blockMapping.push({ templateId: template, blocks: allBlocksForAppliedTemplates[i] });
      });
    }
    yield put(actions.fetchTemplatesAppliedDone(res.result, useMongoDBModel));
  } catch {
    yield put(errorToast('Error fetching templates applied.'));
  }
}

export function* handleFetchTemplatesAppliedForDocuments(
  action: ReturnType<typeof actions.fetchTemplatesAppliedForDocuments>
) {
  try {
    const res: templateApi.ITemplateResponse<ITemplatesAppliedFromMongoDB | ITemplateAppliedFromDynamoDB[]> =
      yield call(templateApi.getTemplatesApplied, action.payload);
    const responseCodes = [200, 204];
    if (!responseCodes.includes(res.status)) {
      throw Error(`Non-Successful Status Code of Fetching Applied Templates POST: ${res.status}`);
    }
    yield put(actions.fetchTemplatesAppliedForDocumentsDone(res.result));
  } catch {
    yield put(errorToast('Error fetching templates applied.'));
  }
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default function* rootSaga() {
  yield all([
    takeEvery(ActionTypes.FETCH_ENVELOPE, handleFetchEnvelope),
    takeEvery(ActionTypes.FETCH_TEMPLATES, handleFetchTemplates),
    takeEvery(ActionTypes.PREVIEW_TEMPLATE, handlePreviewTemplate),
    takeEvery(ActionTypes.FINALIZE_BULK_TEMPLATES, handleBulkTemplates),
    takeEvery(ActionTypes.FETCH_TEMPLATES_APPLIED, handleFetchTemplatesApplied),
    takeEvery(ActionTypes.FETCH_TEMPLATES_APPLIED_FOR_DOCUMENTS, handleFetchTemplatesAppliedForDocuments),
  ]);
}
